
import { useCallback } from 'react';
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { useToast } from './use-toast';
import { Child } from '@/types/child';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '@/utils/logger';

/**
 * Hook to handle child CRUD operations
 */
export function useChildOperations(
  userId: string | undefined, 
  userFamilyId: string | null,
  refreshData: () => void
) {
  const { toast } = useToast();

  const addChild = useCallback(async (newChild: { 
    name: string; 
    birthdate: string;
    photo?: File;
  }) => {
    if (!userId) {
      toast({
        title: "Error",
        description: "You must be logged in to add a child",
        variant: "destructive"
      });
      return;
    }
    
    if (!userFamilyId) {
      toast({
        title: "Error",
        description: "You need to create a family first",
        variant: "destructive"
      });
      return;
    }
    
    if (!newChild.name || !newChild.birthdate) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }
    
    const insertData: any = {
      name: newChild.name,
      birthdate: newChild.birthdate,
      user_id: userId,
      family_id: userFamilyId
    };
    
    try {
      logger.log('Adding new child with data:', insertData);
      
      // If photo is provided, upload it first
      if (newChild.photo) {
        // Generate a unique filename
        const fileExt = newChild.photo.name.split('.').pop();
        const fileName = `${uuidv4()}.${fileExt}`;
        const filePath = `${userId}/${fileName}`;
        
        // Upload the file to Supabase Storage
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('child-photos')
          .upload(filePath, newChild.photo);
        
        if (uploadError) {
          logger.error('Error uploading photo:', uploadError);
          toast({
            title: "Error",
            description: "Failed to upload photo. The child will be added without a photo.",
            variant: "destructive"
          });
        } else {
          // Get the public URL
          const { data: publicUrlData } = supabase.storage
            .from('child-photos')
            .getPublicUrl(filePath);
          
          if (publicUrlData && publicUrlData.publicUrl) {
            insertData.photo_url = publicUrlData.publicUrl;
          }
        }
      }
      
      const { data, error: insertError } = await supabase
        .from('children')
        .insert(insertData)
        .select()
        .single();
      
      if (insertError) {
        logger.error('Error adding child:', insertError);
        toast({
          title: "Error",
          description: "Failed to add child. Please try again.",
          variant: "destructive"
        });
        return;
      }
      
      if (data) {
        toast({
          title: "Success",
          description: "Child profile added successfully!"
        });
        
        // Refresh to get updated data
        refreshData();
      }
    } catch (error) {
      logger.error('Unexpected error adding child:', error);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
    }
  }, [userId, userFamilyId, toast, refreshData]);

  return {
    addChild
  };
}
